import { Loader2, MessageSquareHeart, RefreshCw, TrendingDown, TrendingUp, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { feedbackService, type TFeedback, type TFeedbackAnalytics, type TFeedbackListParams } from "@/api/feedback";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const BAND_STYLES: Record<string, string> = {
  promoter: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400",
  passive: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
  detractor: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400",
  unscored: "bg-muted text-muted-foreground",
};

function DeltaBadge({ value, suffix = "pts" }: { value: number | null | undefined; suffix?: string }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-sm ${up ? "text-emerald-600" : "text-rose-600"}`}>
      <Icon className="h-3.5 w-3.5" />
      {up ? "+" : ""}
      {value} {suffix}
    </span>
  );
}

export function FeedbackPage() {
  const { toast } = useToast();
  const [analytics, setAnalytics] = useState<TFeedbackAnalytics | null>(null);
  const [items, setItems] = useState<TFeedback[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TFeedbackListParams>({});
  const [drillUserId, setDrillUserId] = useState<string | null>(null);
  const [drillItems, setDrillItems] = useState<TFeedback[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [a, l] = await Promise.all([
      feedbackService.getFeedbackAnalytics(30),
      feedbackService.listFeedback({ page, per_page: 20, ...filters }),
    ]);
    setLoading(false);
    if (a.data?.data) setAnalytics(a.data.data);
    if (l.data?.data) {
      setItems(l.data.data.items);
      setTotal(l.data.data.total);
    }
    const err = a.error ?? l.error;
    if (err)
      toast({ variant: "destructive", title: "Failed to load feedback", description: await extractErrorMessage(err) });
  }, [page, filters, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const drillIntoUser = useCallback(async (userId: string) => {
    setDrillUserId(userId);
    setDrillItems(null);
    const { data, error } = await feedbackService.getUserFeedback(userId);
    if (data?.data) setDrillItems(data.data.items);
    if (error) setDrillItems([]);
  }, []);

  const markReviewed = useCallback(
    async (id: string) => {
      const { error } = await feedbackService.markReviewed(id);
      if (error) {
        toast({
          variant: "destructive",
          title: "Couldn't mark reviewed",
          description: await extractErrorMessage(error),
        });
        return;
      }
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, reviewed_at: new Date().toISOString() } : i)));
    },
    [toast]
  );

  const trendData = (analytics?.trend ?? []).map((t) => ({
    week: new Date(t.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    nps: t.nps !== null ? Math.round(t.nps) : null,
    count: t.count,
  }));

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">User Feedback</h1>
          <p className="text-sm text-muted-foreground">
            NPS scores, comments and screenshots from onboarding surfaces.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Analytics cards */}
      {analytics ? (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>NPS (30d)</CardDescription>
                <CardTitle className="text-3xl">{analytics.nps !== null ? Math.round(analytics.nps) : "—"}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <DeltaBadge value={analytics.delta_vs_previous?.nps_delta} /> vs prior 30d
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Responses</CardDescription>
                <CardTitle className="text-3xl">{analytics.total}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <DeltaBadge
                  value={analytics.delta_vs_previous?.count_delta}
                  suffix=""
                />{" "}
                vs prior window
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Promoters / Passives / Detractors</CardDescription>
                <CardTitle className="text-3xl">
                  {analytics.promoters}/{analytics.passives}/{analytics.detractors}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                avg score {analytics.avg_score !== null ? analytics.avg_score.toFixed(1) : "—"} ·{" "}
                <DeltaBadge
                  value={analytics.delta_vs_previous?.avg_score_delta}
                  suffix=""
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>By cohort</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {analytics.by_cohort.map((c) => (
                  <div
                    key={c.cohort}
                    className="flex justify-between"
                  >
                    <span className="capitalize text-muted-foreground">{c.cohort}</span>
                    <span>
                      {c.count} · NPS {c.nps !== null ? Math.round(c.nps) : "—"}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {trendData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Weekly NPS trend</CardTitle>
                <CardDescription>Improvement/regression over the window</CardDescription>
              </CardHeader>
              <CardContent className="h-48">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <BarChart data={trendData}>
                    <XAxis
                      dataKey="week"
                      fontSize={11}
                    />
                    <YAxis
                      fontSize={11}
                      domain={[-100, 100]}
                    />
                    <Tooltip />
                    <Bar
                      dataKey="nps"
                      fill="hsl(var(--primary))"
                      radius={[3, 3, 0, 0]}
                      name="NPS"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      ) : loading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              className="h-28"
            />
          ))}
        </div>
      ) : null}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {(["onboarding", "summary", "stuck", "general"] as const).map((c) => (
          <Button
            key={c}
            size="sm"
            variant={filters.context === c ? "default" : "outline"}
            onClick={() => setFilters((f) => ({ ...f, context: f.context === c ? undefined : c }))}
            className="capitalize"
          >
            {c}
          </Button>
        ))}
        <span className="mx-2 h-4 w-px bg-border" />
        {(["promoter", "passive", "detractor"] as const).map((b) => (
          <Button
            key={b}
            size="sm"
            variant={filters.band === b ? "default" : "outline"}
            onClick={() => setFilters((f) => ({ ...f, band: f.band === b ? undefined : b }))}
            className="capitalize"
          >
            {b}
          </Button>
        ))}
        <Button
          size="sm"
          variant={filters.has_attachment ? "default" : "outline"}
          onClick={() => setFilters((f) => ({ ...f, has_attachment: f.has_attachment ? undefined : true }))}
        >
          With screenshot
        </Button>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center gap-2 p-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<MessageSquareHeart className="h-8 w-8" />}
              title="No feedback yet"
              description="Submissions from the onboarding and summary surfaces will appear here."
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Band</th>
                  <th className="px-4 py-3">Context</th>
                  <th className="px-4 py-3">Cohort</th>
                  <th className="px-4 py-3">Comment</th>
                  <th className="px-4 py-3">Shot</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr
                    key={f.id}
                    className="border-b border-border/50 last:border-0 hover:bg-muted/40"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {new Date(f.created_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3 font-medium">{f.nps_score ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs capitalize ${BAND_STYLES[f.sentiment_band] ?? BAND_STYLES.unscored}`}
                      >
                        {f.sentiment_band}
                      </span>
                    </td>
                    <td className="px-4 py-3 capitalize">{f.context}</td>
                    <td className="px-4 py-3 capitalize">{f.cohort}</td>
                    <td className="max-w-64 truncate px-4 py-3 text-muted-foreground">{f.comment ?? "—"}</td>
                    <td className="px-4 py-3">{f.attachment_file_id ? "📎" : ""}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {f.user_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void drillIntoUser(f.user_id!)}
                          >
                            <User className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {!f.reviewed_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void markReviewed(f.id)}
                          >
                            Mark reviewed
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {total > 20 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page} · {total} total
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * 20 >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Per-user drill-in */}
      {drillUserId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Feedback history — {drillUserId.slice(0, 8)}…</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDrillUserId(null)}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {drillItems === null ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : drillItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No submissions from this user.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {drillItems.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-3"
                  >
                    <span className="text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${BAND_STYLES[f.sentiment_band]}`}>
                      {f.nps_score ?? "—"}
                    </span>
                    <span className="capitalize text-muted-foreground">{f.context}</span>
                    <span className="truncate">{f.comment}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
